import React, { useState, useEffect, useRef } from 'react';
import { Paper, Text, TextInput, Stack } from '@mantine/core';
import { IconArrowDown, IconSearch } from '@tabler/icons-react';
import TagBadge, { tagName } from './TagBadge';

const BouncingArrow = () => (
  <div className="flex justify-center my-2">
    <IconArrowDown
      size={24}
      color="var(--mantine-color-blue-filled)"
      style={{ animation: 'tutorialBounceV 1s ease-in-out infinite' }}
    />
  </div>
);

const FakeSearchButton = ({ language }) => (
  <div style={{
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 999,
    backgroundColor: 'var(--mantine-color-default)',
    pointerEvents: 'none',
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: 999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'var(--mantine-color-default)',
    }}>
      <IconSearch size={22} stroke={1.5} />
    </div>
    <Text size="sm" fw={500} c="dimmed">{language === 'ru' ? 'Поиск' : 'Search'}</Text>
  </div>
);

const TypingInput = ({ language }) => {
  const text = language === 'ru' ? 'Интерстеллар' : 'Interstellar';
  const [displayed, setDisplayed] = useState('');
  const indexRef = useRef(0);

  useEffect(() => {
    indexRef.current = 0;
    setDisplayed('');
    const interval = setInterval(() => {
      indexRef.current++;
      if (indexRef.current <= text.length) {
        setDisplayed(text.slice(0, indexRef.current));
      } else if (indexRef.current > text.length + 15) {
        // Pause then restart
        indexRef.current = 0;
        setDisplayed('');
      }
    }, 120);
    return () => clearInterval(interval);
  }, [text]);

  return (
    <div className="max-w-xs mx-auto" style={{ pointerEvents: 'none' }}>
      <TextInput
        size="lg"
        radius="xl"
        value={displayed}
        readOnly
        leftSection={
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192.904 192.904" width="16px" style={{ fill: "var(--mantine-color-dimmed)" }}>
            <path d="m190.707 180.101-47.078-47.077c11.702-14.072 18.752-32.142 18.752-51.831C162.381 36.423 125.959 0 81.191 0 36.422 0 0 36.423 0 81.193c0 44.767 36.422 81.187 81.191 81.187 19.688 0 37.759-7.049 51.831-18.751l47.079 47.078a7.474 7.474 0 0 0 5.303 2.197 7.498 7.498 0 0 0 5.303-12.803zM15 81.193C15 44.694 44.693 15 81.191 15c36.497 0 66.189 29.694 66.189 66.193 0 36.496-29.692 66.187-66.189 66.187C44.693 147.38 15 117.689 15 81.193z" />
          </svg>
        }
      />
    </div>
  );
};

const FakeTags = ({ language }) => (
  <div className="flex justify-center flex-wrap items-center gap-1" style={{ pointerEvents: 'none' }}>
    {['like', 'veryGood', 'good', 'finished', 'bad', 'inProgress', 'todo'].map((tag) => (
      <TagBadge
        key={tag}
        radius="md"
        size="md"
        color="gray"
        variant="filled"
        className="cursor-pointer"
      >
        {tagName(tag, language)}
      </TagBadge>
    ))}
  </div>
);

const Tutorial = ({ language }) => {
  const isRu = language === 'ru';

  return (
    <Paper shadow="sm" radius="lg" p="md" mb="lg" withBorder>
      <Stack gap={0} align="center">
        <Text size="lg" fw={700} ta="center">
          {isRu ? 'Как пользоваться приложением' : 'How to use the app'}
        </Text>

        <BouncingArrow />

        <Text size="sm" ta="center" maw={360}>
          {isRu
            ? 'Нажмите на кнопку поиска в навигационной панели'
            : 'Click the search button in the navigation bar'}
        </Text>
        <div className="flex justify-center mt-2">
          <FakeSearchButton language={language} />
        </div>

        <BouncingArrow />

        <Text size="sm" ta="center" maw={360}>
          {isRu
            ? 'Введите название фильма, книги или игры'
            : 'Type the title of a movie, book or game'}
        </Text>
        <div className="mt-2 w-full">
          <TypingInput language={language} />
        </div>

        <BouncingArrow />

        <Text size="sm" ta="center">
          {isRu ? 'Нажмите Enter' : 'Press Enter'}
        </Text>

        <BouncingArrow />

        <Text size="sm" ta="center" maw={360}>
          {isRu
            ? 'Выберите произведение из результатов поиска, чтобы открыть его страницу'
            : 'Pick what you searched for to open the art page'}
        </Text>

        <BouncingArrow />

        <Text size="sm" ta="center" maw={360}>
          {isRu
            ? 'На странице произведения выберите подходящий тег, чтобы добавить его в свою библиотеку'
            : 'On the art page, select the appropriate tag to add it to your library'}
        </Text>
        <div className="mt-2">
          <FakeTags language={language} />
        </div>

      </Stack>
    </Paper>
  );
};

export default Tutorial;
